import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MenuPath, Tip } from '../../touchpose/ui';

// Every folder name, engine-version folder, file extension and Unreal menu
// path lives in a components slot — Unreal ships in English, so a translated
// menu path would make the steps impossible to follow.
const M = (s: string) => <MenuPath>{s}</MenuPath>;
const LINK = 'text-blue-link no-underline font-medium';

const Step: React.FC<{ n: number; children: React.ReactNode }> = ({
  n,
  children
}) => (
  <li className="flex gap-[16px] mb-[18px] items-start">
    <span className="shrink-0 w-[28px] h-[28px] rounded-full bg-surface-hover border-[1.5px] border-border-brand flex items-center justify-center font-display text-[13px] font-bold text-blue-link">
      {n}
    </span>
    <p className="mt-[3px] text-[14px] leading-[1.75] text-content-secondary">
      {children}
    </p>
  </li>
);

const InstallationDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary max-w-[760px]">
      <h1 className="font-display text-[28px] font-extrabold text-content-primary mb-[10px] tracking-[-0.4px]">
        {t('subdivsDocs.layout.installation')}
      </h1>
      <p className="text-[15px] leading-[1.7] text-content-secondary mb-[8px]">
        {t('subdivsDocs.installation.intro')}
      </p>

      <h2
        id="install"
        className="font-display text-[20px] font-bold text-content-primary mt-[36px] mb-[14px] tracking-[-0.3px]"
      >
        {t('subdivsDocs.installation.installTitle')}
      </h2>
      <ol className="p-0 m-0 list-none">
        <Step n={1}>
          <Trans
            i18nKey="subdivsDocs.installation.step1"
            components={{
              mp: (
                <Link to="/management?getSubdivs=1" className={LINK}>
                  {t('header.myProducts')}
                </Link>
              ),
              win: <strong>Windows</strong>,
              mac: <strong>macOS</strong>
            }}
          />
        </Step>
        <Step n={2}>
          <Trans
            i18nKey="subdivsDocs.installation.step2"
            components={{
              u57: M('UE_5.7'),
              u58: M('UE_5.8'),
              folder: M('SquarebitSubdivs'),
              plugins: M('Plugins'),
              tree: M('<YourProject>/Plugins/SquarebitSubdivs/')
            }}
          />
        </Step>
        <Step n={3}>{t('subdivsDocs.installation.step3')}</Step>
        <Step n={4}>
          <Trans
            i18nKey="subdivsDocs.installation.step4"
            components={{
              menu: M('Edit ▸ Plugins ▸ Project ▸ SquarebitSubdivs')
            }}
          />
        </Step>
      </ol>

      <Tip label={t('subdivsDocs.installation.tipLabel')}>
        <Trans
          i18nKey="subdivsDocs.installation.tipBody"
          components={{ uplugin: M('.uplugin') }}
        />
      </Tip>

      <h2
        id="verify"
        className="font-display text-[20px] font-bold text-content-primary mt-[36px] mb-[14px] tracking-[-0.3px]"
      >
        {t('subdivsDocs.installation.verifyTitle')}
      </h2>
      <p className="text-[14px] leading-[1.75] text-content-secondary mb-[12px]">
        <Trans
          i18nKey="subdivsDocs.installation.verifyBody"
          components={{
            menu: M('Edit ▸ Plugins'),
            plug: <strong>Squarebit Subdivs</strong>,
            comp: <strong>Squarebit Subdiv</strong>,
            add: M('Add')
          }}
        />
      </p>

      <div className="border-t border-surface-hover mt-[36px] pt-[20px]">
        <p className="text-[14px] leading-[1.7] text-content-secondary">
          <Trans
            i18nKey="subdivsDocs.installation.nextBody"
            components={{
              lic: (
                <Link to="/squarebit-subdivs/docs/licensing" className={LINK} />
              )
            }}
          />
        </p>
      </div>
    </article>
  );
};

export default InstallationDoc;
